import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import apiService from '../services/api';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Card, Spinner } from '../components/ui';
import { McpConfirmation } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';

/**
 * L'approbation d'une operation demandee par un agent IA.
 *
 * C'est la moitie visible de MRTR en mode URL (plan MCP, palier 2, C17). En revision
 * `2026-07-28`, le client de l'agent affiche lui-meme le resume et attend l'accord ;
 * cette page sert aux clients restes a `2025-06-18`, ou ce mecanisme n'existe pas et ou
 * le modele rejouerait de lui-meme sans que personne n'ait rien vu.
 *
 * Cette page **n'execute rien**. Elle enregistre une decision ; c'est l'outil MCP qui
 * lance l'operation a son prochain rejeu, apres verification de son sceau. L'API rend le
 * meme 404 qu'une demande introuvable si le compte connecte n'est pas celui pour qui elle
 * a ete emise : une adresse recopiee dans un canal partage ne donne donc rien.
 */
const McpConfirmationPage: React.FC = () => {
  // @ts-ignore -- TS2589: useTranslation type instantiation too deep
  const { t } = (useTranslation as any)('organisations');
  const { requestId } = useParams<{ requestId: string }>();

  const [demande, setDemande] = useState<McpConfirmation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const r = await apiService.getMcpConfirmation(requestId);
      setDemande(r);
      setError(null);
    } catch (err: unknown) {
      Logger.error('MCP confirmation load failed', err);
      setError(t('mcpConfirmation.notFound'));
    } finally {
      setLoading(false);
    }
  }, [requestId, t]);

  useEffect(() => {
    charger();
  }, [charger]);

  const decider = async (approuve: boolean) => {
    if (!requestId) return;
    setEnvoi(true);
    setError(null);
    try {
      await apiService.decideMcpConfirmation(requestId, approuve);
      // On relit au lieu de deviner : l'etat fait foi cote serveur, et une decision est
      // definitive -- l'afficher depuis la reponse laisserait croire qu'on peut revenir
      // dessus en rechargeant.
      await charger();
      toast.success(approuve ? t('mcpConfirmation.approved') : t('mcpConfirmation.refused'));
    } catch (err: unknown) {
      Logger.error('MCP confirmation decide failed', err);
      setError(getApiErrorMessage(err, t('mcpConfirmation.decideError')));
      await charger();
    } finally {
      setEnvoi(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-1">
        <Spinner />
      </div>
    );
  }

  const tranchee = demande && demande.etat !== 'en_attente';

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-1 px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <Card>
          <Card.Header>
            <h1 className="text-[15px] font-semibold text-text-primary">
              {t('mcpConfirmation.title')}
            </h1>
          </Card.Header>
          <Card.Body>
            {!demande ? (
              <p className="text-[12px] text-text-secondary">
                {error || t('mcpConfirmation.notFound')}
              </p>
            ) : tranchee ? (
              <div className="space-y-3">
                <p className="text-[12px] text-text-primary">
                  {/* `relevee` a ecrase l'information cote serveur -- volontairement :
                      un agent qui rejoue doit d'abord savoir que la conclusion est
                      tiree. La page reste donc neutre plutot que d'annoncer un accord
                      sur une demande qui avait peut-etre ete refusee. */}
                  {demande.etat === 'refusee' && t('mcpConfirmation.doneRefused')}
                  {demande.etat === 'approuvee' && t('mcpConfirmation.doneApproved')}
                  {demande.etat === 'relevee' && t('mcpConfirmation.doneHandled')}
                </p>
                <p className="text-[12px] text-text-secondary">
                  {t('mcpConfirmation.doneHint')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-[12px] text-text-primary">{demande.message}</p>
                {Object.keys(demande.resume || {}).length > 0 && (
                  <dl className="space-y-1 rounded-md bg-surface-2 px-3 py-2">
                    {Object.entries(demande.resume).map(([cle, valeur]) => (
                      <div key={cle} className="flex justify-between gap-4">
                        <dt className="text-[12px] text-text-tertiary">{cle}</dt>
                        <dd className="text-[12px] text-text-primary text-right">
                          {String(valeur)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                <p className="text-[12px] text-text-tertiary">
                  {t('mcpConfirmation.finalHint')}
                </p>
                {error && <p className="text-[12px] text-danger">{error}</p>}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    disabled={envoi}
                    onClick={() => decider(false)}
                  >
                    {t('mcpConfirmation.refuse')}
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    disabled={envoi}
                    onClick={() => decider(true)}
                  >
                    {envoi ? t('mcpConfirmation.sending') : t('mcpConfirmation.approve')}
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default McpConfirmationPage;
